// The only file the rest of your site imports.
//
//   import ProjectsRoom from './components/ProjectsRoom'
//   <ProjectsRoom onSelect={(project) => navigate(project.href)} />
//
// This eager part is tiny (an IntersectionObserver, the SVG loader, some CSS). three.js
// and react-three-fiber live in Room.jsx, which is only fetched, mounted and rendered
// when the section is within ~900px of the viewport, and is paused when off-screen.
import { Component, lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react'
import Loader from './Loader'
import defaultProjects from './projects'
import './ProjectsRoom.css'

const loadRoom = () => import('./Room')
const Room = lazy(loadRoom)

// Call this early (e.g. when the user hovers the "Work" link in your nav) to start
// downloading the 3D chunk before they scroll down.
export const preloadProjectsRoom = () => {
  loadRoom()
}

function webglAvailable() {
  try {
    const c = document.createElement('canvas')
    return !!(c.getContext('webgl2') || c.getContext('webgl'))
  } catch {
    return false
  }
}

function shapeToClipPath(points) {
  if (!Array.isArray(points) || points.length === 0) {
    return 'polygon(14% 0%, 86% 0%, 100% 100%, 0% 100%)'
  }

  const polygons = points
    .map(([x, y]) => {
      const px = ((x + 0.5) * 100)
      const py = ((0.5 - y) * 100)
      return `${Math.max(0, Math.min(100, px))}% ${Math.max(0, Math.min(100, py))}%`
    })
    .join(', ')

  return `polygon(${polygons})`
}

function isMobileSafari() {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent || ''
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  const isSafari = /Safari\//.test(ua) && !/CriOS|Chrome|Android/.test(ua)
  return isIOS && isSafari
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const on = () => setReduced(mq.matches)
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', on)
      return () => mq.removeEventListener('change', on)
    }
    if (typeof mq.addListener === 'function') {
      mq.addListener(on)
      return () => mq.removeListener(on)
    }
    return undefined
  }, [])
  return reduced
}

// If WebGL is missing or the 3D chunk throws, show a plain grid of the same projects.
class RoomBoundary extends Component {
  state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  componentDidCatch(error) {
    console.error('[ProjectsRoom] falling back to the static grid:', error)
    this.props.onFail?.()
  }
  render() {
    return this.state.failed ? null : this.props.children
  }
}

function StaticGrid({ projects, onSelect, mobile = false, activeId = null, onActivate = null, onDeactivate = null }) {
  return (
    <ul className={`pr-static ${mobile ? 'pr-static--mobile' : ''}`}>
      {projects.map((p, index) => {
        const isActive = mobile && activeId === p.id
        const cardStyle = mobile
          ? {
              animationDelay: `${index * 80}ms`,
              '--paper-tilt': `${index % 2 === 0 ? -1.6 : 1.8}deg`,
              '--paper-hover-tilt': `${index % 2 === 0 ? -3.8 : 3.6}deg`,
              '--paper-shape': shapeToClipPath(p.shape?.points),
            }
          : undefined

        return (
          <li key={p.id} className={mobile ? 'pr-static__item pr-static__item--mobile' : 'pr-static__item'}>
            <a
              href={p.href}
              onMouseEnter={() => {
                if (mobile && onActivate) onActivate(p.id)
              }}
              onMouseLeave={() => {
                if (mobile && onDeactivate) onDeactivate()
              }}
              onTouchStart={() => {
                if (mobile && onActivate) onActivate(p.id)
              }}
              onFocus={() => {
                if (mobile && onActivate) onActivate(p.id)
              }}
              onBlur={() => {
                if (mobile && onDeactivate) onDeactivate()
              }}
              onClick={(e) => {
                if (mobile && onActivate) {
                  onActivate(p.id)
                }
                if (onSelect) {
                  e.preventDefault()
                  onSelect(p)
                }
              }}
              className={mobile ? `pr-static__link pr-static__link--mobile ${isActive ? 'is-active' : ''}` : 'pr-static__link'}
              style={cardStyle}
            >
              <img
                src={p.frames[0]}
                alt=""
                loading="lazy"
                className={mobile ? 'pr-static__image pr-static__image--mobile' : 'pr-static__image'}
                style={mobile ? { clipPath: `var(--paper-shape)` } : undefined}
              />
              <span className="pr-static__kicker">{p.kicker}</span>
              <strong className="pr-static__title">{p.title}</strong>
              <span className="pr-static__summary">{p.summary}</span>
            </a>
          </li>
        )
      })}
    </ul>
  )
}

export default function ProjectsRoom({ projects = defaultProjects, onSelect, height = '100vh', className = '' }) {
  const hostRef = useRef(null)
  const reduced = usePrefersReducedMotion()
  const mobilePulseRef = useRef(null)

  const [mobileActiveId, setMobileActiveId] = useState(null)
  const [fallback, setFallback] = useState(() => {
    if (typeof document === 'undefined') return true
    return !webglAvailable() || isMobileSafari() || window.innerWidth < 768
  })
  const [shouldMount, setShouldMount] = useState(false) // near the viewport at least once
  const [onScreen, setOnScreen] = useState(false) // currently visible
  const [sceneReady, setSceneReady] = useState(false) // chunk + textures + first mount done
  const [loaderGone, setLoaderGone] = useState(false)

  // Start loading when near; track whether it is actually on screen.
  useEffect(() => {
    const el = hostRef.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setShouldMount(true)
      setOnScreen(true)
      return undefined
    }
    const near = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setShouldMount(true)
          near.disconnect()
        }
      },
      { rootMargin: '900px 0px' },
    )
    const visible = new IntersectionObserver(([e]) => setOnScreen(e.isIntersecting), { threshold: 0.01 })
    near.observe(el)
    visible.observe(el)
    return () => {
      near.disconnect()
      visible.disconnect()
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const onResize = () => {
      if (isMobileSafari() || window.innerWidth < 768) {
        setFallback(true)
      } else if (!webglAvailable()) {
        setFallback(true)
      } else {
        setFallback(false)
      }
    }
    onResize()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const handleReady = useCallback(() => setSceneReady(true), [])
  const handleFail = useCallback(() => setFallback(true), [])
  const handleGone = useCallback(() => setLoaderGone(true), [])
  const handleMobilePreview = useCallback((id) => {
    setMobileActiveId(id)
    if (mobilePulseRef.current) {
      window.clearTimeout(mobilePulseRef.current)
    }
    if (typeof window !== 'undefined') {
      mobilePulseRef.current = window.setTimeout(() => setMobileActiveId(null), 450)
    }
  }, [])

  useEffect(() => () => {
    if (mobilePulseRef.current && typeof window !== 'undefined') {
      window.clearTimeout(mobilePulseRef.current)
    }
  }, [])

  const ready = sceneReady

  return (
    <section
      ref={hostRef}
      className={`pr-host ${className}`}
      style={{ height }}
      data-ready={ready ? 'true' : 'false'}
      aria-label="Selected projects"
    >
      {fallback ? (
        <>
          <StaticGrid
            projects={projects}
            onSelect={onSelect}
            mobile={isMobileSafari() || (typeof window !== 'undefined' && window.innerWidth < 768)}
            activeId={mobileActiveId}
            onActivate={handleMobilePreview}
            onDeactivate={() => setMobileActiveId(null)}
          />
          {isMobileSafari() || (typeof window !== 'undefined' && window.innerWidth < 768) ? (
            <div className="pr-mobile-note">This interactive experience is only available on desktop.</div>
          ) : null}
        </>
      ) : (
        <>
          {shouldMount && (
            <RoomBoundary onFail={handleFail}>
              <Suspense fallback={null}>
                <Room
                  projects={projects}
                  onSelect={onSelect}
                  // Render until the first frames are done (compiles shaders off-screen),
                  // then only while visible.
                  running={onScreen || !sceneReady}
                  reduced={reduced}
                  onReady={handleReady}
                />
              </Suspense>
            </RoomBoundary>
          )}
          {!loaderGone && <Loader done={ready} onGone={handleGone} />}
        </>
      )}
    </section>
  )
}
